import { useEffect, useMemo, useRef, useState } from "react";
import { BellRing, ChevronLeft, Flag, Layers, RotateCcw } from "lucide-react";
import { useTurnRoom } from "../../lib/useTurnRoom";
import type { GameTableProps } from "../game-core";
import { ginRummyAdapter } from "./adapter";
import { bestMelds } from "./melds";
import type { CardId } from "./cards";
import type { DrawSource, GinState, HandResult } from "./engine";

const SUIT_SYMBOL: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };
const SUIT_NAME: Record<string, string> = { S: "spades", H: "hearts", D: "diamonds", C: "clubs" };
const RANK_NAME: Record<string, string> = {
  A: "Ace", T: "Ten", J: "Jack", Q: "Queen", K: "King",
  "2": "Two", "3": "Three", "4": "Four", "5": "Five", "6": "Six", "7": "Seven", "8": "Eight", "9": "Nine",
};
const rankLabel = (card: CardId) => (card[0] === "T" ? "10" : card[0]);
const isRed = (card: CardId) => card[1] === "H" || card[1] === "D";
const cardName = (card: CardId) => `${RANK_NAME[card[0]]} of ${SUIT_NAME[card[1]]}`;

function Card({ card, selected, onClick, disabled, small }: { card: CardId; selected?: boolean; onClick?: () => void; disabled?: boolean; small?: boolean }) {
  const face = (
    <>
      <b>{rankLabel(card)}</b>
      <span>{SUIT_SYMBOL[card[1]]}</span>
    </>
  );
  const className = `card${isRed(card) ? " red" : ""}${selected ? " selected" : ""}${small ? " small" : ""}`;
  if (!onClick) return <div className={className} aria-label={cardName(card)}>{face}</div>;
  return (
    <button className={className} onClick={onClick} disabled={disabled} aria-pressed={selected} aria-label={`${cardName(card)}${selected ? ", selected" : ""}`}>{face}</button>
  );
}

const isSet = (meld: CardId[]) => meld.every((c) => c[0] === meld[0][0]);

export function GinRummyTable({ roomId, roomToken, myName, opponentName, opponentAvatarUrl, onExit, onProgress }: GameTableProps) {
  const room = useTurnRoom(ginRummyAdapter, "gin-rummy", roomId, roomToken, myName);
  const { me, state, view, isMyTurn } = room;

  const [drawn, setDrawn] = useState<{ card: CardId; source: DrawSource } | null>(null);
  const [selected, setSelected] = useState<CardId | null>(null);
  const [nudgeSent, setNudgeSent] = useState(false);

  // Clear the local (unsubmitted) turn composition only when a move is actually accepted
  // (revision advances). Keying on isMyTurn too could wipe a freshly-drawn card mid-turn.
  useEffect(() => { setDrawn(null); setSelected(null); setNudgeSent(false); }, [state?.revision]);

  // Read onProgress through a ref: depending on its (per-render) identity here would loop.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  useEffect(() => { if (state && me) onProgressRef.current?.(ginRummyAdapter.summarize(state, me)); }, [state, me]);

  const result = view?.hand.result ?? null;
  const terminal = view != null && ginRummyAdapter.isTerminal(view);
  // Only render the deal from real server state, or from the host's own opening on the first move.
  const showTable = view != null && (state != null || isMyTurn);

  const myScore = view && me ? view.match.points[me] ?? 0 : 0;
  const oppScore = view && me ? view.match.points[view.players[0] === me ? view.players[1] : view.players[0]] ?? 0 : 0;

  const myHand = view && me ? view.hand.hands[me] ?? [] : [];
  const displayHand = useMemo(() => (drawn ? [...myHand, drawn.card] : myHand), [myHand, drawn]);
  const grouping = useMemo(() => bestMelds(displayHand), [displayHand]);

  // Deadwood if the currently selected card were discarded (drives the Knock/Gin affordance).
  const afterDiscard = useMemo(
    () => (selected ? bestMelds(displayHand.filter((c) => c !== selected)).deadwoodValue : null),
    [displayHand, selected],
  );
  const canKnock = drawn != null && selected != null && afterDiscard != null && afterDiscard <= 10;
  const isGin = canKnock && afterDiscard === 0;

  const commit = (type: "turn" | "knock") => {
    if (!drawn || !selected) return;
    void room.submit({ version: 1, type, draw: drawn.source, discard: selected });
  };

  return (
    <article className="gin-table">
      <header className="table-bar">
        <button className="icon-button" onClick={onExit} aria-label="Back to games"><ChevronLeft /></button>
        <div className="table-title"><p className="eyebrow">Gin Rummy</p><h1>{opponentName ?? "Your game"}</h1></div>{opponentAvatarUrl && <img className="table-opponent-avatar" src={opponentAvatarUrl} alt={`${opponentName ?? "Opponent"}’s avatar`} />}
        <div className="score-to-100" aria-label={`You ${myScore}, opponent ${oppScore}, first to 100`}>
          <span className="you">{myScore}</span><i>/100</i><span className="them">{oppScore}</span>
        </div>
      </header>

      <StatusLine room={room} drawn={drawn != null} terminal={terminal} result={result} me={me} opponentName={opponentName} />

      {!showTable ? (
        <div className="gin-waiting"><Layers /><p>{room.waiting === "connecting" ? "Connecting to the table…" : result ? "" : "Waiting for the deal…"}</p></div>
      ) : result ? (
        <ResultView
          result={result}
          me={me!}
          myName={myName}
          opponentName={opponentName}
          terminal={terminal}
          isMyTurn={isMyTurn}
          nudgeSent={nudgeSent}
          onNudge={() => void room.nudge().then(() => setNudgeSent(true)).catch(() => undefined)}
          onNext={() => void room.submit({ version: 1, type: "next_hand" })}
          onExit={onExit}
        />
      ) : (
        <>
          <section className="opponent-row" aria-label={`${opponentName ?? "Opponent"} holds ${view!.hand.hands[view!.players[0] === me ? view!.players[1] : view!.players[0]].length} cards`}>
            <span className="row-label">{opponentName ?? "Opponent"}</span>
            <div className="card-backs">{Array.from({ length: view!.hand.hands[view!.players[0] === me ? view!.players[1] : view!.players[0]].length }, (_, i) => <i key={i} />)}</div>
          </section>

          <section className="piles" aria-label="Draw and discard piles">
            <button
              className="pile stock"
              disabled={!isMyTurn || drawn != null || view!.hand.drawPile.length === 0}
              onClick={() => setDrawn({ card: view!.hand.drawPile[0], source: "stock" })}
              aria-label={`Stock, ${view!.hand.drawPile.length} cards. Draw.`}
            >
              <i /><small>{view!.hand.drawPile.length}</small>
            </button>
            <div className="pile-labels"><span>Stock</span><span>Discard</span></div>
            <button
              className="pile discard"
              disabled={!isMyTurn || drawn != null || view!.hand.discardPile.length === 0}
              onClick={() => setDrawn({ card: view!.hand.discardPile[view!.hand.discardPile.length - 1], source: "discard" })}
              aria-label={`Discard pile, top card ${cardName(view!.hand.discardPile[view!.hand.discardPile.length - 1])}. Take it.`}
            >
              <Card card={view!.hand.discardPile[view!.hand.discardPile.length - 1]} small />
            </button>
          </section>

          <section className="my-hand" aria-label="Your hand">
            <div className="hand-heading">
              <span className="row-label">{myName}</span>
              <span className="deadwood-chip">Deadwood {grouping.deadwoodValue}</span>
            </div>
            <div className="meld-rails">
              {grouping.melds.map((meld, i) => (
                <div key={i} className={`meld-rail ${isSet(meld) ? "set" : "run"}`}>
                  <span className="rail-label">{isSet(meld) ? "Set" : "Run"}</span>
                  <div className="rail-cards">{meld.map((card) => <DiscardCard key={card} card={card} drawn={drawn} selected={selected} setSelected={setSelected} />)}</div>
                </div>
              ))}
              {grouping.deadwood.length > 0 && (
                <div className="meld-rail deadwood">
                  <span className="rail-label">Deadwood</span>
                  <div className="rail-cards">{grouping.deadwood.map((card) => <DiscardCard key={card} card={card} drawn={drawn} selected={selected} setSelected={setSelected} />)}</div>
                </div>
              )}
            </div>
          </section>

          <footer className="table-actions">
            {isMyTurn && !drawn && <p className="action-hint">Draw from the stock or take the discard.</p>}
            {isMyTurn && drawn && (
              <div className="discard-actions">
                <button className="primary-button" disabled={!selected} onClick={() => commit("turn")}>
                  {selected ? `Discard ${rankLabel(selected)}${SUIT_SYMBOL[selected[1]]}` : "Select a card to discard"}
                </button>
                <button
                  className="knock-button"
                  disabled={!canKnock}
                  title={canKnock ? (isGin ? "End the hand with gin" : "End the hand by knocking") : "Knock requires 10 or less deadwood after your discard"}
                  onClick={() => commit("knock")}
                >
                  {isGin ? "Gin!" : "Knock"}
                </button>
              </div>
            )}
            {!terminal && view && (
              <>
                {!isMyTurn && (
                  <button className="ghost-button" disabled={nudgeSent} onClick={() => {
                    void room.nudge().then(() => setNudgeSent(true)).catch(() => undefined);
                  }}>
                    <BellRing size={16} /> {nudgeSent ? "Reminder sent" : "Nudge opponent"}
                  </button>
                )}
                <button className="ghost-button" onClick={() => { if (confirm("Resign this match? Your opponent wins immediately.")) void room.submit({ version: 1, type: "resign", playerId: me ?? undefined }); }}>
                  <Flag size={16} /> Resign
                </button>
              </>
            )}
          </footer>
        </>
      )}

      {room.error && <p className="form-error" role="alert">{room.error}</p>}
    </article>
  );
}

/** A card in the hand that becomes selectable for discard once the player has drawn. */
function DiscardCard({ card, drawn, selected, setSelected }: {
  card: CardId;
  drawn: { card: CardId; source: DrawSource } | null;
  selected: CardId | null;
  setSelected: (card: CardId | null) => void;
}) {
  const selectable = drawn != null;
  // Cannot discard the very card you just took from the discard pile.
  const forbidden = drawn?.source === "discard" && card === drawn.card;
  if (!selectable) return <Card card={card} />;
  return <Card card={card} selected={selected === card} disabled={forbidden} onClick={() => setSelected(selected === card ? null : card)} />;
}

function StatusLine({ room, drawn, terminal, result, me, opponentName }: {
  room: ReturnType<typeof useTurnRoom<GinState, import("./engine").GinMove>>;
  drawn: boolean;
  terminal: boolean;
  result: HandResult | null;
  me: string | null;
  opponentName?: string;
}) {
  let message: string;
  let tone = "waiting";
  if (room.waiting === "connecting") message = "Connecting…";
  else if (room.waiting === "opponent" && room.isMyTurn) {
    message = drawn ? "Finish your opening turn, then invite your opponent." : "Take the opening turn, then invite your opponent.";
    tone = "your-turn";
  }
  else if (room.waiting === "opponent") message = "Waiting for your opponent to join.";
  else if (terminal && room.state && me) {
    const won = room.state.match.winnerId === me;
    message = won ? "You won the match!" : `${opponentName ?? "Your opponent"} won the match.`;
    tone = won ? "your-turn" : "waiting";
  } else if (result) { message = "Hand over — see the score below."; }
  else if (room.isMyTurn) { message = drawn ? "Discard, or knock if your deadwood is 10 or less." : "Your turn — draw a card."; tone = "your-turn"; }
  else message = `Waiting for ${opponentName ?? "your opponent"}.`;

  return (
    <div className={`turn-status ${tone}`} role="status" aria-live="polite">
      <span className="turn-dot" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function ResultView({ result, me, myName, opponentName, terminal, isMyTurn, nudgeSent, onNudge, onNext, onExit }: {
  result: HandResult;
  me: string;
  myName: string;
  opponentName?: string;
  terminal: boolean;
  isMyTurn: boolean;
  nudgeSent: boolean;
  onNudge: () => void;
  onNext: () => void;
  onExit: () => void;
}) {
  const scorerLabel = result.scorerId == null ? "Nobody" : result.scorerId === me ? myName : opponentName ?? "Opponent";
  const headline =
    result.kind === "draw" ? "Drawn hand — no score"
      : result.kind === "gin" ? `${scorerLabel} went gin`
        : result.kind === "undercut" ? `${scorerLabel} undercut!`
          : `${scorerLabel} knocked`;

  return (
    <section className="hand-result">
      <div className="result-headline"><strong>{headline}</strong>{result.points > 0 && <span>+{result.points}</span>}</div>
      {result.kind !== "draw" && (
        <p className="result-detail">Knocker deadwood {result.knockerDeadwood} · Opponent {result.opponentDeadwood}</p>
      )}
      <div className="revealed-hands">
        {Object.entries(result.hands).map(([pid, cards]) => (
          <div key={pid} className="revealed">
            <span className="row-label">{pid === me ? myName : opponentName ?? "Opponent"}</span>
            <div className="revealed-cards">{[...cards].map((card) => <Card key={card} card={card} small />)}</div>
          </div>
        ))}
      </div>
      <div className="table-actions">
        {terminal ? (
          <button className="primary-button" onClick={onExit}><RotateCcw size={16} /> Back to games</button>
        ) : isMyTurn ? (
          <button className="primary-button" onClick={onNext}>Deal the next hand</button>
        ) : (
          <>
            <p className="action-hint">Waiting for {opponentName ?? "your opponent"} to deal the next hand.</p>
            <button className="ghost-button" disabled={nudgeSent} onClick={onNudge}>
              <BellRing size={16} /> {nudgeSent ? "Reminder sent" : "Nudge opponent"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
